/*
Pure logic for the Sent emails tab (SentEmailsComponents/*).

The tab reads the sent-email archive through ps-back's getSentEmails /
getSentEmail (ps-email-archive/CONTRACT.md §5). Everything here that can be
decided without React lives in this file so sentEmails.test.js can pin it down.

CommonJS on purpose, same reasoning as cmsTabs.js: the colocated test runs
under plain `node` with no webpack/babel toolchain (this package has no test
framework); components import it through babel's CJS interop.
*/

// Status values written by the archive ingest (CONTRACT.md §2), in the order the
// toolbar lists them. Labels are what the admin reads; values are what the API
// filters on.
const STATUS_OPTIONS = [
  { value: 'SENT', label: 'Sent' },
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'DELAYED', label: 'Delayed' },
  { value: 'BOUNCED', label: 'Bounced' },
  { value: 'COMPLAINED', label: 'Complaint' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'RENDERING_FAILED', label: 'Rendering failed' },
];

// Chip tones, the same four Loyalty uses (chip--good|warn|bad|quiet). SENT is
// quiet rather than good: it only means SES accepted it, not that it arrived.
const STATUS_TONES = {
  SENT: 'quiet',
  DELIVERED: 'good',
  DELAYED: 'warn',
  BOUNCED: 'bad',
  COMPLAINED: 'bad',
  REJECTED: 'bad',
  RENDERING_FAILED: 'bad',
};

// The raw-send kinds ps-back tags its hand-built emails with (CONTRACT.md §1).
// Templated sends use their templateId as the kind instead, so the toolbar also
// offers an "Other…" free-text box for those.
const KIND_OPTIONS = [
  'password-reset',
  'account-verification',
  'create-password',
  'purchase',
  'subscription-renewal-failed',
  'subscription-change',
  'ps-voucher',
  'presale-voucher',
  'early-bird-code',
  'contact-form',
  'internal-report',
];

const statusTone = status => STATUS_TONES[status] || 'quiet';

const statusLabel = (status) => {
  if (!status) return '—';
  const option = STATUS_OPTIONS.find(item => item.value === status);
  return option ? option.label : String(status);
};

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const trimmed = value => (typeof value === 'string' ? value.trim() : '');

/*
Turns the toolbar's filters (plus paging) into getSentEmails variables:
  - every text value is trimmed; empty values are dropped rather than sent as ""
  - recipient is lowercased, because the archive stores it lowercased and
    matches it exactly (CONTRACT.md §4)
  - from/to stay YYYY-MM-DD (UTC, inclusive on the server); anything else is
    dropped instead of being sent as a filter the server would reject
  - cursor is only sent when there is one (first page = no cursor)
*/
const buildSentEmailsVariables = (filters, cursor, limit) => {
  const source = filters || {};
  const cleaned = {};

  const recipient = trimmed(source.recipient).toLowerCase();
  if (recipient) cleaned.recipient = recipient;

  ['subject', 'status', 'kind'].forEach((key) => {
    const value = trimmed(source[key]);
    if (value) cleaned[key] = value;
  });

  ['from', 'to'].forEach((key) => {
    const value = trimmed(source[key]);
    if (DATE_ONLY.test(value)) cleaned[key] = value;
  });

  const variables = { filters: cleaned };
  if (cursor) variables.cursor = cursor;
  if (limit) variables.limit = limit;
  return variables;
};

const pad = number => String(number).padStart(2, '0');

const toUtcDate = date => `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;

/*
The last 7 days, today included, as the YYYY-MM-DD strings a native
<input type="date"> holds. Computed in UTC because the server reads from/to as
UTC calendar days — a Madrid admin just after midnight would otherwise ask for
a "today" that has not started in UTC yet.
*/
const defaultDateRange = (now) => {
  const today = now instanceof Date ? now : new Date(now || Date.now());
  const sixDaysAgo = new Date(today.getTime() - (6 * 24 * 60 * 60 * 1000));
  return { from: toUtcDate(sixDaysAgo), to: toUtcDate(today) };
};

/*
Where PS/RDL email artwork lives, measured on 2026-09-29 over the code templates
(ps-back/assets) and the bodies archived that day: our own static buckets and asset
hosts, plus Splio's image CDN used by older templates. Nothing per recipient. The
S3 entries name the bucket path, never the bare shared S3 host, which would load
images from any bucket. Every other host, including tracking pixels, stays blocked,
so opening an email here never registers as an open by the customer. Same approach
as Mission Control (accessticket-missioncontrol #392).
*/
const IMAGE_SOURCES = [
  'https://s3-eu-west-1.amazonaws.com/public.primaverasound.com/',
  'https://s3.eu-west-1.amazonaws.com/public.primaverasound.com/',
  'https://s3-eu-west-1.amazonaws.com/assets.primaverasound.com/',
  'https://s3.eu-west-1.amazonaws.com/assets.primaverasound.com/',
  'https://assets.primaverasound.com',
  'https://assets-img.primaverasound.com',
  'https://assets.rockdelux.com',
  'https://cdn.message-builder.splio.pro',
];

const CSP_META = `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data: ${IMAGE_SOURCES.join(' ')}; style-src 'unsafe-inline'">`;

// Sends every link click to a new browsing context, which the sandbox (no
// allow-popups) refuses — so clicking a link in the preview does nothing
// instead of loading the target page inside the admin.
const BLANK_BASE = '<base target="_blank">';

// What goes at the top of <head>: the policy first, so it governs everything after it.
const HEAD_PREAMBLE = `${CSP_META}${BLANK_BASE}`;

// Any <meta http-equiv> of the email: refresh would navigate the frame, a second CSP
// could only confuse. Matching the attribute name (not its value) also catches values
// written with entities, e.g. http-equiv="&#114;efresh".
const META_HTTP_EQUIV = /<meta\b[^>]*\bhttp-equiv\b[^>]*>/gi;
const BASE_TAG = /<base\b[^>]*>/gi;
// An element's own target (e.g. target="_self") would override our base and let a
// clicked link load the page inside the frame; without it every link opens a popup,
// which the sandbox refuses.
const TARGET_ATTR = /(<(?:a|area|form)\b[^>]*?)\s+target\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi;
// The email's own doctype (after an optional XML prolog) keeps its rendering mode;
// email HTML usually relies on the "almost standards" table/image layout.
const LEADING_DOCTYPE = /^\s*(?:<\?xml[^>]*>\s*)?(<!doctype[^>]*>)/i;

/*
Prepares an archived email body for <iframe sandbox="" srcDoc>.

The sandbox already blocks scripts, forms, popups and same-origin access. The
CSP meta adds: no network at all — so a viewed email cannot fire its own
tracking pixels or pull remote images — while inline styles and data: images
still render.

Our <html><head> is always emitted first, before any byte of the email, so the
policy governs the whole document no matter what precedes the email's own
<head> (an <img> before it, a commented-out "<head>", a <header>…). The
parser then ignores the email's second <html>/<head> tags and still applies
its <title>, <meta> and <style>, which land in our head or body.

CSP does not govern navigation, so the email's own <base> (would retarget
every link) and <meta http-equiv> (refresh would navigate the frame on a
timer) are removed, and a <base target="_blank"> of ours makes link clicks
inert (see BLANK_BASE); the email's own target attributes are removed so no
link can override that and load a page inside the frame.
*/
const withCsp = (html) => {
  if (html === null || html === undefined) return '';
  let body = String(html).replace(BASE_TAG, '').replace(META_HTTP_EQUIV, '');
  // Repeated because an element can carry more than one target attribute.
  let previous;
  do {
    previous = body;
    body = body.replace(TARGET_ATTR, '$1');
  } while (body !== previous);
  let doctype = '';
  const lead = body.match(LEADING_DOCTYPE);
  if (lead) {
    [, doctype] = lead;
    body = body.slice(lead[0].length);
  }
  return `${doctype}<html><head>${HEAD_PREAMBLE}</head>${body}`;
};

/*
The drawer's timeline: the send itself, then every SES event the ingest stored,
oldest first. The send is not an event on the row (SES "Send" events are
ignored by design), so it is rebuilt from sentAt.
*/
const buildTimeline = (email) => {
  if (!email) return [];
  const events = (email.events || [])
    .filter(Boolean)
    .map(event => ({ type: event.type, at: event.at, detail: event.detail || '' }));
  const sent = email.sentAt ? [{ type: 'SENT', at: email.sentAt, detail: '' }] : [];
  const time = value => (Number.isNaN(Date.parse(value)) ? Infinity : Date.parse(value));
  return [...sent, ...events.slice().sort((a, b) => time(a.at) - time(b.at))];
};

const redactionNote = (count) => {
  const n = Number(count) || 0;
  if (n <= 0) return '';
  return n === 1 ? '1 link redacted' : `${n} links redacted`;
};

module.exports = {
  STATUS_OPTIONS,
  KIND_OPTIONS,
  statusTone,
  statusLabel,
  buildSentEmailsVariables,
  defaultDateRange,
  withCsp,
  buildTimeline,
  redactionNote,
  CSP_META,
  IMAGE_SOURCES,
  HEAD_PREAMBLE,
};
